import moment from 'moment'
import { FRACTIONS, FRACTIONS_TYPES } from '@/utils/constants/drs/fractions'
import { OPERATOR_CODES, OPERATOR_EP_CODE, CONTRACT_STATUSES, CONTRACT_LOCATION_STATUSES } from '@/utils/constants/drs/operators'

const CONTRACT_CONFLICT_STATUS = 409

const getFractionById = (id) => {
    return Object.values(FRACTIONS).find(f => f.id === id)
}

const filteredEPOperators = (operators) => {
    return operators.filter(op =>
        op.features.some(feature => feature.code === OPERATOR_EP_CODE)
    )
}

const feeToCents = (fee) => {
    return Number(fee || 0) * 100
}

const getContractStatus = (contract, today) => {
    if (!contract) return CONTRACT_STATUSES.NOT_REGISTERED

    const hasDetails = !!contract.contractDetails?.length
    const end = contract.endDate ? moment(contract.endDate) : null
    const isActive = !end || end.isAfter(today, 'day')

    if (isActive && !hasDetails) return CONTRACT_STATUSES.INCOMPLETE
    if (isActive) return CONTRACT_STATUSES.ACTIVE

    return CONTRACT_STATUSES.ARCHIVED
}

const getContractToDisplay = (operatorContracts, today) => {
    const activeContract = operatorContracts.find(contract => {
        const end = contract.endDate ? moment(contract.endDate) : null
        return !end || end.isAfter(today, 'day')
    })

    if (activeContract) return activeContract

    // Return the archived contract with the most recent endDate
    return [...operatorContracts]
        .filter(contract => contract.endDate)
        .sort((a, b) => moment(b.endDate).valueOf() - moment(a.endDate).valueOf())[0] || null
}

const hasMissingCollectionPoint = (subscriptions) => {
    return subscriptions.some(sub =>
        sub.locStatus !== CONTRACT_LOCATION_STATUSES.INACTIVE &&
        !sub.externalData?.collection_point_id
    )
}

const state = () => ({
    operators: [],
    status: 'idle',
    fractions: null,
    createContractsStatus: 'idle',
    operatorSubs: [],
    operatorSubsStatus: 'idle',
    filters: {
        query: {
            type: 'search-input',
            value: '',
            searchParamsTooltip: ['nazwie operatora']
        }
    },
    operatorContract: null,
    allOperators: []
})

const mutations = {
    SET_OPERATORS(state, payload) {
        state.operators = payload
    },
    SET_STATUS(state, payload) {
        state.status = payload
    },
    SET_FRACTIONS(state, payload) {
        state.fractions = payload
    },
    SET_CREATE_CONTRACTS_STATUS(state, payload) {
        state.createContractsStatus = payload
    },
    SET_OPERATOR_SUBS(state, payload) {
        state.operatorSubs = payload
    },
    SET_OPERATOR_SUBS_STATUS(state, payload) {
        state.operatorSubsStatus = payload
    },
    SET_FILTER(state, payload) {
        state.filters[payload.filter].value = payload.value
    },
    RESET_FILTERS(state, payload) {
        state.filters = payload
    },
    SET_OPERATOR_CONTRACT(state, payload) {
        state.operatorContract = payload
    },
    SET_ALL_OPERATORS(state, payload) {
        state.allOperators = payload
    }
}

const actions = {
    async fetchOperators({ commit, state }) {
        // skip fetching if operators are already loaded
        if (state.operators.length) return

        try {
            commit('SET_STATUS', 'loading')

            const { status, data } = await this.$api.get(
                'drs/api/proxy/subscriptions/[drs_user_id]/services?operatorService=true'
            )

            if (status !== 200 || !data?.services) {
                throw new Error(data.message || 'Error fetchOperators')
            }

            // Filter services that have a feature OPERATOR_EP_CODE
            const filteredServices = filteredEPOperators(data.services).map(service => {
                const operatorFeature = service.features.find(f => f.code === 'OPERATOR_ID')
                return {
                    ...service,
                    serviceId: +operatorFeature.description
                }
            })

            commit('SET_OPERATORS', filteredServices)
        } catch (error) {
            console.error(error)
        } finally {
            commit('SET_STATUS', 'idle')
        }
    },
    async fetchAndMapFractions({ commit, state }) {
        // skip fetching if fractions are already loaded
        if (state.fractions) return
        try {
            const { status, data } = await this.$api.get('drs/api/proxy/fraction')

            if (status !== 200 || !data?.fractions) {
                throw new Error(data.message || 'Error fetchAndMapFractions')
            }

            // map fractions from API and add details
            const mappedFractions = data.fractions.map(fr => ({
                ...getFractionById(fr.id),
                ...(fr.name && { name: fr.name })
            }))

            // add mix
            const fractions = [
                ...mappedFractions,
                { ...FRACTIONS[FRACTIONS_TYPES.MIX] }
            ]

            commit('SET_FRACTIONS', fractions)
        } catch (error) {
            console.error(error)
        }
    },
    async createContract(_, payload) {
        try {
            const { data, status } = await this.$api.post(
                'drs/api/operator_contracts',
                payload,
                { config: { noCancel: true } }
            )

            return { data, status }
        } catch (error) {
            const status = error?.response?.status
            const data = error?.response?.data

            if (status === CONTRACT_CONFLICT_STATUS) {
                return { data, status }
            }

            console.error(error)
            throw error
        }
    },
    async createContractDetail(_, payload) {
        try {
            const { data } = await this.$api.post(
                'drs/api/contract_details',
                payload,
                { config: { noCancel: true } }
            )
            if (!data?.id) throw new Error(`Error POST drs/api/contract_details for in location ${payload.locationId}`)
            return data
        } catch (error) {
            console.error(error)
            throw error
        }
    },
    async createContractWithDetails({ dispatch }, { operatorServiceId, companyId, locationId, contract }) {
        const enabledFractions = contract.fractions.filter(f => f.enabled)
        if (!enabledFractions.length) return false

        let hasError = false
        let contractId = null

        try {
            const createContractResponse = await dispatch('createContract', {
                defaultHandlingFee: 0,
                startDate: contract.startDate,
                endDate: contract.indefinite ? null : contract.endDate,
                fractions: enabledFractions.map(f => f.id),
                companyId,
                operatorServiceId
            })

            if (createContractResponse.status !== CONTRACT_CONFLICT_STATUS) {
                contractId = createContractResponse.data.id
            } else {
                const detail = JSON.parse(createContractResponse.data.detail)
                contractId = detail.conflictContractIds?.[0]

                if (!contractId) {
                    return true
                }

                // Fetch existing contract
                const existingContract = await dispatch('fetchContract', contractId)

                const existingFractions = existingContract.fractions || []
                const newFractions = enabledFractions.map(f => f.id)

                // Update contract
                await dispatch('updateContract', {
                    contractId,
                    payload: {
                        defaultHandlingFee: 0,
                        startDate: contract.startDate,
                        endDate: contract.indefinite ? null : contract.endDate,
                        fractions: [...new Set([
                            ...(existingFractions || []),
                            ...(newFractions || [])
                        ])]
                    }
                })
            }
        } catch {
            hasError = true
        }

        // Create contract details
        if (contractId) {
            const setPayload = (fraction) => ({
                locationId,
                fractionId: fraction.id,
                handlingFee: feeToCents(fraction.handlingFee),
                operatorContract: `/api/operator_contracts/${contractId}`
            })

            const [first, ...rest] = enabledFractions
            try {
                await dispatch('createContractDetail', setPayload(first))
            } catch {
                hasError = true
            }

            await Promise.all(rest.map(async (fraction) => {
                try {
                    await dispatch('createContractDetail', setPayload(fraction))
                } catch {
                    hasError = true
                }
            }))
        }

        // Save external data
        try {
            await dispatch('saveExternalData', {
                locationId,
                operatorServiceId,
                collectionPointId: contract.collectionPointId,
                retailerToken: contract.retailerToken
            })
        } catch {
            dispatch(
                'addNotification',
                {
                    type: 'danger',
                    content: 'Przy zapisie numeru punktu zbiórki wystąpił błąd'
                },
                { root: true }
            )
        }

        return hasError
    },
    async createContracts({ commit, dispatch }, { operatorServiceId, locations, contractsData }) {
        commit('SET_CREATE_CONTRACTS_STATUS', 'loading')
        let hasError = false

        for (const loc of locations) {
            const contract = contractsData[loc.id]
            if (!contract) continue

            const error = await dispatch('createContractWithDetails', {
                operatorServiceId,
                companyId: loc.companyId,
                locationId: loc.id,
                contract
            })

            if (error && !hasError) hasError = true
        }

        commit('SET_CREATE_CONTRACTS_STATUS', hasError ? 'error' : 'ok')
    },
    async fetchAndMapOperatorSubscriptions({ commit }, payload) {
        try {
            commit('SET_OPERATOR_SUBS_STATUS', 'loading')
            const { status, data } = await this.$api.get(
                'drs/api/proxy/subscriptions/[drs_user_id]/services/drs/operator',
                payload
            )

            if (status !== 200 || !data?.length) {
                throw new Error(data.message || 'Error fetchOperatorSubscriptions')
            }

            // Filter operators that have a feature OPERATOR_EP_CODE, map to id and subscription id
            const filtered = filteredEPOperators(data).map(({ id, subscriptions }) => ({
                id,
                subscriptions: subscriptions.map(({ entityData }) => entityData.id)
            }))
            commit('SET_OPERATOR_SUBS', filtered)
        } catch (error) {
            console.error(error)
        } finally {
            commit('SET_OPERATOR_SUBS_STATUS', 'idle')
        }
    },
    async saveExternalData({ state, getters }, { locationId, operatorServiceId, collectionPointId, retailerToken }) {
        const operatorCode = state.operators.find(el => +el.id === +operatorServiceId)?.code

        await this.$api.put(
            `drs/api/proxy/drs/locations/operators/${locationId}/data/external`,
            {
                operator_id: getters.getServiceIdByOperatorId(operatorServiceId),
                collection_point_id: collectionPointId,
                ...(operatorCode === OPERATOR_CODES.POLKA && { retailer_token: retailerToken })
            }
        )
    },
    async fetchExternalData (_, { locationId, operatorServiceIds }) {
        try {
            if (!locationId || !operatorServiceIds?.length) return

            const { data } = await this.$api.get(
                `drs/api/proxy/drs/locations/operators/${locationId}/data/external`,
                { params: { operators_ids: operatorServiceIds.join(',') } }
            )
            if (!data?.operators) throw new Error('Failed fetchExternalData')

            return data.operators
        } catch (error) {
            console.error(error)
        }
    },
    async loadAndMapContract({ commit, dispatch, getters, rootGetters }, opId) {
        const { data: allContracts, status } = await dispatch('fetchContracts') || { data: [] }
        if (status !== 200) {
            commit('SET_OPERATOR_CONTRACT', null)
            return
        }

        const operatorContracts = allContracts.filter(contract =>
            +contract.operatorServiceId === +opId && +contract.companyId === +rootGetters['drs/companiesData'][0].id
        )

        const mappedContract = await dispatch('mapContract', {
            operatorContracts,
            opId
        })

        const operatorExternalId = getters.getServiceIdByOperatorId(opId)

        if (!operatorExternalId) {
            commit('SET_OPERATOR_CONTRACT', {
                ...mappedContract,
                subscriptions: mappedContract.subscriptions.map(sub => ({
                    ...sub,
                    externalData: null
                })),
                hasMissingCollectionPoint: false
            })
            return
        }
        const subscriptions = await Promise.all(
            mappedContract.subscriptions.map(async sub => {
                const operators = await dispatch('fetchExternalData', {
                    locationId: sub.locationId,
                    operatorServiceIds: [operatorExternalId]
                })

                const externalData = operators?.find(op => +op.id === +operatorExternalId) || null

                return {
                    ...sub,
                    externalData
                }
            })
        )

        commit('SET_OPERATOR_CONTRACT', {
            ...mappedContract,
            subscriptions,
            hasMissingCollectionPoint: hasMissingCollectionPoint(subscriptions)
        })
    },
    async fetchContracts(_, payload) {
        try {
            const { status, data } = await this.$api.get('drs/api/operator_contracts', {
                params: {
                    pagination: false,
                    ...payload
                }
            })

            if (status !== 200 || !data) {
                throw new Error(data.message || 'Error fetchOperatorContracts')
            }

            return { data, status }
        } catch (error) {
            console.error(error.response)
            return error.response
        }
    },
    async mapContract({ rootGetters }, payload) {
        const { operatorContracts, opId } = payload

        const locations = rootGetters['drs/shopsWithActiveService']
        const today = moment()

        const contract = getContractToDisplay(operatorContracts, today)
        const status = getContractStatus(contract, today)

        const subscriptions = locations.map(loc => {
            const details = contract?.contractDetails?.filter(d =>
                +d.locationId === +loc.id
            ) || []

            let locStatus = CONTRACT_LOCATION_STATUSES.INACTIVE

            if (details.length) {
                locStatus = status === CONTRACT_STATUSES.ARCHIVED
                    ? CONTRACT_LOCATION_STATUSES.ARCHIVED
                    : CONTRACT_LOCATION_STATUSES.ACTIVE
            }

            return {
                companyId: loc.companyId,
                fractions: details.map(d => ({
                    id: d.fractionId,
                    handlingFee: parseFloat((d.handlingFee / 100).toFixed(3)),
                    handlingFeeRvm: d.handlingFeeRvm
                        ? parseFloat((d.handlingFeeRvm / 100).toFixed(3))
                        : null,
                    contractId: d.id
                })),
                fullAddress: loc.fullAddress,
                full_name: loc.full_name,
                locationId: loc.id,
                locStatus,
                contractId: contract?.id || null
            }
        })

        return {
            operatorServiceId: opId,
            id: contract?.id || null,
            startDate: contract?.startDate || null,
            endDate: contract?.endDate || null,
            defaultHandlingFee: contract?.defaultHandlingFee ?? 0,
            defaultHandlingFeeRvm: contract?.defaultHandlingFeeRvm ?? 0,
            fractions: contract?.fractions || [],
            status,
            subscriptions
        }
    },
    async updateContract(_, { contractId, payload }) {
        return await this.$api.patch(
            `drs/api/operator_contracts/${contractId}`,
            payload
        )
    },
    async updateContractDetail(_, { contractId, handlingFee }) {
        await this.$api.patch(
            `drs/api/contract_details/${contractId}`,
            { handlingFee }
        )
    },
    async deleteContractDetail(_, payload) {
        await this.$api.delete(
            `drs/api/contract_details/${payload}`
        )
    },
    async processContractChanges({ dispatch }, { prev, curr, operatorServiceId }) {
        let hasError = false
        const requests = []
        const {
            fractions: prevFracs,
            startDate: prevStart,
            endDate: prevEnd,
            collectionPointId: prevCollectionPointId,
            retailerToken: prevRetailerToken,
            locationId
        } = prev
        const {
            fractions: currFracs,
            startDate: currStart,
            endDate: currEnd,
            contractId: currContractId,
            collectionPointId: currCollectionPointId,
            retailerToken: currRetailerToken,
            indefinite
        } = curr

        // check if any fractions was added/removed
        const prevIds = prevFracs.map(f => f.id)
        const currIds = currFracs.map(f => f.id)
        const removedIds = prevIds.filter(id => !currIds.includes(id))
        const addedIds = currIds.filter(id => !prevIds.includes(id))

        // update contract if dates or fraction set have changed
        const endChanged = indefinite
            ? prevEnd
            : !moment(prevEnd).isSame(moment(currEnd))
        const datesChanged = !moment(prevStart).isSame(moment(currStart)) || endChanged
        const shouldUpdateContract = datesChanged || removedIds.length || addedIds.length
        shouldUpdateContract && requests.push(() => dispatch('updateContract', {
            contractId: currContractId,
            payload: {
                defaultHandlingFee: 0,
                endDate: indefinite ? null : currEnd,
                startDate: currStart,
                fractions: currIds
            }
        }))

        // delete contract detail for removed fractions
        removedIds.forEach(removedId => {
            const fr = prevFracs.find(({ id }) => id === removedId)
            fr && requests.push(() => dispatch('deleteContractDetail', fr.contractId))
        })

        // create contract detail for newly added fractions
        addedIds.forEach(addedId => {
            const fr = currFracs.find(({ id }) => id === addedId)
            fr && requests.push(() => dispatch('createContractDetail', {
                locationId,
                fractionId: fr.id,
                handlingFee: feeToCents(fr.handlingFee),
                operatorContract: `/api/operator_contracts/${currContractId}`
            }))
        })

        // update contract detail for updated handlingFee in existing fractions
        currFracs.forEach(fr => {
            const prevFr = prevFracs.find(({ id }) => id === fr.id)
            const shouldUpdateFee = prevFr && +prevFr.handlingFee !== +fr.handlingFee
            shouldUpdateFee && requests.push(() => dispatch('updateContractDetail', {
                contractId: prevFr.contractId,
                handlingFee: feeToCents(fr.handlingFee)
            }))
        })

        // update collectionPointId and retailerToken if have changed
        const shouldUpdateExternalData =
            (prevCollectionPointId !== currCollectionPointId) ||
            (prevRetailerToken !== currRetailerToken)

        shouldUpdateExternalData && requests.push(() => dispatch('saveExternalData', {
            locationId,
            operatorServiceId,
            collectionPointId: currCollectionPointId,
            retailerToken: currRetailerToken
        }))

        const results = await Promise.allSettled(requests.map(r => r()))
        hasError = results.some(r => r.status === 'rejected')

        return { hasError }
    },
    async editLocationContractAndUpdateData({ dispatch, commit }, payload) {
        commit('SET_CREATE_CONTRACTS_STATUS', 'loading')

        const { hasError } = await dispatch('processContractChanges', payload)
        await dispatch('loadAndMapContract', payload.operatorServiceId)

        commit('SET_CREATE_CONTRACTS_STATUS', hasError ? 'error' : 'ok')
    },
    async createContractAndUpdateData({ dispatch, commit }, payload) {
        commit('SET_CREATE_CONTRACTS_STATUS', 'loading')

        const hasError = await dispatch('createContractWithDetails', payload)
        await dispatch('loadAndMapContract', payload.operatorServiceId)

        commit('SET_CREATE_CONTRACTS_STATUS', hasError ? 'error' : 'ok')
    },
    async deleteContract(_, contractId) {
        await this.$api.delete(`drs/api/operator_contracts/${contractId}`)
    },

    async fetchContract(_, contractId) {
        try {
            const { status, data } = await this.$api.get(`drs/api/operator_contracts/${contractId}`)

            if (status !== 200 || !data) {
                throw new Error(data.message || 'Error fetchContract')
            }

            return data
        } catch (error) {
            console.error(error)
        }
    },

    async updateLocationExternalData({ state, dispatch, commit, getters }, {
        locationId,
        operatorServiceId,
        collectionPointId,
        retailerToken
    }) {
        await dispatch('saveExternalData', {
            locationId,
            operatorServiceId,
            collectionPointId,
            retailerToken
        })

        const operatorExternalId = getters.getServiceIdByOperatorId(operatorServiceId)

        if (!operatorExternalId) {
            throw new Error('Missing operatorExternalId')
        }

        const operators = await dispatch('fetchExternalData', {
            locationId,
            operatorServiceIds: [operatorExternalId]
        })

        const externalData = operators?.find(op => +op.id === +operatorExternalId) || null

        const subscriptions = state.operatorContract.subscriptions.map(sub =>
            +sub.locationId === +locationId
                ? { ...sub, externalData }
                : sub
        )

        commit('SET_OPERATOR_CONTRACT', {
            ...state.operatorContract,
            subscriptions,
            hasMissingCollectionPoint: hasMissingCollectionPoint(subscriptions)
        })
    },

    async fetchAllOperators({ commit, state }) {
        if (state.allOperators.length) return

        try {
            const { status, data } = await this.$api.get(
                'drs/api/proxy/subscriptions/[drs_user_id]/services?operatorService=true'
            )

            if (status !== 200 || !data?.services) {
                throw new Error(data?.message || 'Error fetchAllOperators')
            }

            const mappedServices = data.services.map(service => {
                const operatorFeature = service.features.find(f => f.code === 'OPERATOR_ID')
                return {
                    ...service,
                    serviceId: +operatorFeature.description
                }
            })

            commit('SET_ALL_OPERATORS', mappedServices)
        } catch (error) {
            console.error(error)
        }
    }
}

const getters = {
    getOperators: state => state.operators,
    getStatus: state => state.status,
    getFractions: state => state.fractions,
    getCreateContractsStatus: state => state.createContractsStatus,
    getFilters: state => state.filters,
    getOperatorSubs: state => state.operatorSubs,
    getOperatorSubsStatus: state => state.operatorSubsStatus,
    getServiceIdByOperatorId: state => operatorId => {
        return state.operators.find(op => op.id === +operatorId)?.serviceId
    },
    getServiceByOperatorId: state => opId => {
        return state.operators.find(op => op.serviceId === opId)
    },
    getOperatorById: state => id => state.operators.find(op => op.id === +id),
    operatorContract: state => state.operatorContract,
    getAllOperators: state => state.allOperators || []
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
